/**
 * KingStar product page (sections/kingstar-product.liquid).
 * Gallery, size / quantity selection, ADD TO CART through Shopify.actions (so Horizon's cart icon and
 * drawer update), SIZE GUIDE table from the kingstar.measurements text, and the READ MORE description.
 */
class KingStarProduct extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('[data-kingstar-variants]')?.textContent || '[]');
    const pressed = this.querySelector('[data-kingstar-variant][aria-pressed="true"]');
    this.variant =
      this.variants.find((v) => String(v.id) === pressed?.dataset.kingstarVariant) ??
      this.variants.find((v) => v.available) ??
      this.variants[0];
    this.quantity = 1;
    this.index = 0;

    this.slides = this.querySelector('[data-kingstar-slides]');
    this.count = this.slides?.children.length ?? 0;

    this.zoom = this.querySelector('[data-kingstar-zoom]');
    // PC (mouse): a click anywhere closes the view. Phones: CLOSE or the area outside the images, so a tap while
    // pinching or scrolling does not close it.
    this.zoom?.addEventListener('click', (event) => {
      if (event.target === this.zoom || window.matchMedia('(hover: hover) and (pointer: fine)').matches) this.zoom.close();
    });
    // The browser's back button (and the back swipe) closes the view instead of leaving the page.
    this.zoom?.addEventListener('close', () => {
      if (window.history.state?.kingstarZoom) window.history.back();
    });
    window.addEventListener('popstate', this.#onPopState);

    this.addEventListener('click', this.#onClick);
    this.slides?.addEventListener('scroll', this.#onScroll, { passive: true });
    document.addEventListener('keydown', this.#onKey);

    this.#buildGuide();
    this.#clampDescription();
    this.#render();
    this.#playVideos();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.#onKey);
    window.removeEventListener('popstate', this.#onPopState);
  }

  #onPopState = () => {
    if (this.zoom?.open && !window.history.state?.kingstarZoom) this.zoom.close();
  };

  get #wide() {
    return window.matchMedia('(min-width: 900px)').matches;
  }

  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const size = target.closest('[data-kingstar-variant]');
    if (size && !size.disabled) {
      this.variant = this.variants.find((v) => String(v.id) === size.dataset.kingstarVariant) ?? this.variant;
      this.quantity = 1;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(this.variant.id));
      window.history.replaceState(window.history.state, '', url);
      this.#render();
      return;
    }

    const qty = target.closest('[data-kingstar-qty]');
    if (qty) {
      const next = this.quantity + Number(qty.dataset.kingstarQty);
      this.quantity = Math.min(Math.max(next, 1), this.variant?.max ?? 1);
      this.#render();
      return;
    }

    if (target.closest('[data-kingstar-add]')) return this.#add();

    const step = target.closest('[data-kingstar-step]');
    if (step) return this.#show(this.index + Number(step.dataset.kingstarStep));

    const thumb = target.closest('[data-kingstar-thumb]');
    if (thumb) return this.#show(Number(thumb.dataset.kingstarThumb));

    if (target.closest('[data-kingstar-zoom-close]')) return this.zoom?.close();

    // A tap or click on an image opens the full screen view; a click on a video is for its player controls.
    // A swipe or pinch fires no click, so the phone's own gestures are left alone.
    const image = target.closest('[data-kingstar-slides] > img');
    if (image) return this.#openZoom(image);

    if (target.closest('[data-kingstar-more]')) return this.#toggleDescription();
  };

  /** @param {Element} image - the clicked slide image; the view opens at the same image */
  #openZoom(image) {
    if (!this.zoom || !this.slides) return;
    const index = [...this.slides.querySelectorAll(':scope > img')].indexOf(image);
    const target = this.zoom.querySelectorAll('[data-kingstar-zoom-image]')[index];
    this.zoom.showModal();
    window.history.pushState({ ...window.history.state, kingstarZoom: true }, '');
    // Focus the view itself, not the CLOSE button (no focus ring on a tap); Tab still reaches CLOSE.
    this.zoom.focus();
    target?.scrollIntoView({ block: 'start', behavior: 'instant' });
  }

  #onKey = (event) => {
    if (!this.#wide || this.count < 2) return;
    if (event.target instanceof Element && event.target.closest('input, select, textarea, dialog[open]')) return;
    if (document.querySelector('dialog[open]')) return;
    if (event.key === 'ArrowLeft') this.#show(this.index - 1);
    if (event.key === 'ArrowRight') this.#show(this.index + 1);
  };

  #onScroll = () => {
    if (!this.slides) return;
    const index = Math.round(this.slides.scrollLeft / this.slides.clientWidth);
    if (index !== this.index) {
      this.index = index;
      this.#renderGallery();
    }
  };

  /** @param {number} index */
  #show(index) {
    if (!this.slides || this.count < 1) return;
    this.index = (index + this.count) % this.count;
    this.slides.scrollTo({ left: this.index * this.slides.clientWidth, behavior: 'instant' });
    this.#renderGallery();
  }

  #renderGallery() {
        this.querySelectorAll('.kingstar-product__bars > span').forEach((bar, i) =>
      bar.toggleAttribute('data-active', i === this.index)
    );
    this.querySelectorAll('[data-kingstar-thumb]').forEach((thumb, i) =>
      thumb.toggleAttribute('data-active', i === this.index)
    );
    const counter = this.querySelector('[data-kingstar-counter]');
    if (counter) counter.textContent = `${this.index + 1} / ${this.count}`;
    this.#playVideos();
  }

  /** The video of the shown slide plays (muted, looped); the others pause. */
  #playVideos() {
    if (!this.slides) return;
    [...this.slides.children].forEach((slide, i) => {
      const video = slide.matches('[data-kingstar-video]') ? slide.querySelector('video') : null;
      if (!video) return;
      if (i === this.index) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  }

  #render() {
    const variant = this.variant;
    if (!variant) return;

    this.querySelectorAll('[data-kingstar-variant]').forEach((button) =>
      button.setAttribute('aria-pressed', String(button.dataset.kingstarVariant === String(variant.id)))
    );

    const price = this.querySelector('[data-kingstar-price]');
    if (price) price.textContent = variant.price;
    const compare = this.querySelector('[data-kingstar-compare]');
    if (compare) {
      compare.textContent = variant.compare ?? '';
      compare.hidden = !variant.compare;
    }

    const value = this.querySelector('[data-kingstar-qty-value]');
    if (value) value.textContent = String(this.quantity);
    const [minus, plus] = this.querySelectorAll('[data-kingstar-qty]');
    if (minus) minus.disabled = this.quantity <= 1;
    if (plus) plus.disabled = this.quantity >= variant.max;

    const ctaPrice = this.querySelector('[data-kingstar-cta-price]');
    if (ctaPrice) ctaPrice.textContent = this.#money(variant.priceCents * this.quantity);

    this.#highlightGuide();
  }

  /** @param {number} cents */
  #money(cents) {
    return `¥${Math.round(cents / 100).toLocaleString('ja-JP')}`;
  }

  /** ADD TO CART: adds, then opens the cart drawer so the addition is plain to see (2026-10-14). */
  async #add() {
    const variant = this.variant;
    if (!variant?.available) return;

    const buttons = this.querySelectorAll('[data-kingstar-add]');
    const error = this.querySelector('[data-kingstar-error]');
    buttons.forEach((button) => button.setAttribute('aria-busy', 'true'));
    if (error) error.hidden = true;

    try {
      const actions = window.Shopify?.actions;
      if (actions?.updateCart) {
        const result = await actions.updateCart({
          lines: [{ merchandiseId: `gid://shopify/ProductVariant/${variant.id}`, quantity: this.quantity }],
        });
        const problem = result?.userErrors?.[0]?.message ?? result?.warnings?.[0]?.message;
        if (result?.userErrors?.length) throw new Error(problem);
        await actions.openCart();
      } else {
        const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variant.id, quantity: this.quantity }] }),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message);
        // No drawer to open without Shopify.actions: show the cart page instead.
        window.location.href = `${window.Shopify?.routes?.root ?? '/'}cart`;
      }
    } catch (e) {
      if (error) {
        error.textContent = e instanceof Error && e.message ? e.message : 'カートに追加できませんでした。';
        error.hidden = false;
      }
    } finally {
      buttons.forEach((button) => button.removeAttribute('aria-busy'));
    }
  }

  /**
   * SIZE GUIDE: "[ Sサイズ ]" starts a column, "着丈約62.5cm" adds a row value. What is written wins; lines that
   * cannot be read (no label) are shown under the table.
   * If nothing can be read as a table, the text stays as written (already rendered by Liquid).
   */
  #buildGuide() {
    const source = this.querySelector('[data-kingstar-guide-source]');
    const body = this.querySelector('[data-kingstar-guide-body]');
    if (!source || !body) return;

    const text = String(JSON.parse(source.textContent || '""'));
    const columns = [];
    const keys = [];
    const notes = [];
    let column = null;

    for (const raw of text.split(/\r?\n/)) {
      const line = raw.trim();
      if (!line) continue;
      const head = line.match(/^[\[［]\s*(.+?)\s*[\]］]$/);
      if (head) {
        column = { size: head[1], values: {} };
        columns.push(column);
        continue;
      }
      // One line can hold several items ("ウエスト約66〜72cm, 股上約36cm"). The label is the text before "約" or the
      // first digit; the value is the rest as written ("58～62(アジャスターで調節可能)"), without 約 and cm.
      const items = line.split(/[,，、]/).map((part) => part.trim()).filter(Boolean);
      const cells = items.map((part) => {
        const at = part.search(/約|\d/);
        if (at <= 0) return null;
        const key = part.slice(0, at).replace(/[\s:：]+$/, '');
        const value = part
          .slice(at)
          .replace(/約/g, '')
          .replace(/\s*(cm|㎝|センチ)(?=$|[\s(（～〜~])/gi, '')
          .trim();
        return key ? { key, value } : null;
      });
      if (!column || cells.some((cell) => !cell)) {
        notes.push(line);
        continue;
      }
      for (const { key, value } of cells) {
        if (!keys.includes(key)) keys.push(key);
        column.values[key] = value;
      }
    }

    if (!columns.length || !keys.length) return;

    const escape = (s) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
    const head = columns.map((c) => `<th scope="col">${escape(c.size)}</th>`).join('');
    const rows = keys
      .map(
        (key) =>
          `<tr><th scope="row">${escape(key)}</th>${columns
            .map((c) => `<td>${escape(c.values[key] ?? '—')}</td>`)
            .join('')}</tr>`
      )
      .join('');
    body.innerHTML =
      `<table class="kingstar-product__table"><thead><tr><th></th>${head}</tr></thead><tbody>${rows}</tbody></table>` +
      (notes.length ? `<p class="kingstar-product__guide-notes">${notes.map(escape).join('<br>')}</p>` : '');

    const unit = this.querySelector('.kingstar-product__unit');
    if (unit) unit.hidden = false;
    this.guideSizes = columns.map((c) => this.#sizeKey(c.size));
  }

  /** "SIZE(S)", "Sサイズ", "サイズ28(XS)" -> "S", "S", "XS" (the part in brackets wins). */
  #sizeKey(label) {
    const text = String(label).normalize('NFKC');
    const bracket = text.match(/\(([^)]+)\)/);
    return (bracket ? bracket[1] : text).replace(/size|サイズ|\s/gi, '').toUpperCase();
  }

  #highlightGuide() {
    const table = this.querySelector('.kingstar-product__table');
    if (!table || !this.guideSizes || !this.variant) return;
    const key = this.#sizeKey(this.variant.title);
    const column = this.guideSizes.indexOf(key);
    table.querySelectorAll('tr').forEach((row) => {
      [...row.children].forEach((cell, i) => cell.toggleAttribute('data-active', column >= 0 && i === column + 1));
    });
  }

  #clampDescription() {
    const body = this.querySelector('[data-kingstar-desc]');
    const more = this.querySelector('[data-kingstar-more]');
    if (!body || !more) return;
    body.setAttribute('data-clamped', '');
    if (body.scrollHeight - body.clientHeight > 8) {
      more.hidden = false;
    } else {
      body.removeAttribute('data-clamped');
    }
  }

  #toggleDescription() {
    const body = this.querySelector('[data-kingstar-desc]');
    const more = this.querySelector('[data-kingstar-more]');
    if (!body || !more) return;
    const open = body.toggleAttribute('data-clamped');
    more.textContent = open ? 'More +' : 'Less −';
  }
}

if (!customElements.get('kingstar-product')) customElements.define('kingstar-product', KingStarProduct);
